<template>
  <el-menu
    ref="topNav"
    mode="horizontal"
    class="top-nav-box"
    menu-trigger="click"
    @select="handleSubmenuSelect"
    @close="handleMenuClose"
  >
    <!-- Home -->
    <el-menu-item
      index="1"
      class="home-box"
      :title="$t('Home')"
      @click="() => $router.push('/')"
    >
      <font-awesome-icon :icon="homeIcon" />
    </el-menu-item>

    <!-- Date -->
    <el-menu-item
      index="2"
      class="date-box"
    >
      <i class="el-icon-date" />
      <span slot="title">{{ now }}</span>
    </el-menu-item>

    <!-- FRA Flag -->
    <el-menu-item
      index="6"
      class="flag-box"
    >
      <img
        alt="france-flag"
        src="@/assets/france32.png"
        @click="changeLocale('fr')"
      >
    </el-menu-item>

    <!-- ENG Flag -->
    <el-menu-item
      index="5"
      class="flag-box"
    >
      <img
        alt="uk-flag"
        src="@/assets/uk32.png"
        @click="changeLocale('en')"
      >
    </el-menu-item>

    <!-- DEPARTMENTS DROPDOWN -->
    <DepartmentSubmenu ref="departmentSubmenu" />

    <!-- User Menu -->
    <el-submenu
      index="3"
      class="dropdown-menu-box"
      popper-class="top-nav-box"
    >
      <span
        slot="title"
      >
        {{ name }}

        <font-awesome-icon :icon="userIcon" />
      </span>

      <el-menu-item
        v-if="$route.path!=='/'"
        index="3-1"
        class="dropdown-menu-item"
        @click="$router.push('/')"
      >
        <i class="el-icon-monitor" />
        {{ $t('Dashboard') }}
      </el-menu-item>

      <el-menu-item
        index="3-2"
        class="dropdown-menu-item"
        @click="$router.push('/profile')"
      >
        <i class="el-icon-user" />
        {{ $t('Profile') }}
      </el-menu-item>

      <el-menu-item
        index="3-3"
        class="dropdown-menu-item"
        @click="$router.push('/admin')"
      >
        <i class="el-icon-setting" />
        {{ $t('Administration') }}
      </el-menu-item>

      <el-menu-item
        index="3-4"
        class="dropdown-menu-item"
        @click="$router.push('/changelog')"
      >
        <i class="el-icon-document" />
        {{ $t('Changelog') }}
      </el-menu-item>

      <el-menu-item
        index="3-5"
        class="dropdown-menu-item"
        @click="signOut"
      >
        <i class="el-icon-switch-button" />
        {{ $t('Sign out') }}
      </el-menu-item>
    </el-submenu>
  </el-menu>
</template>

<script lang="ts">
import { Locale, SetUserLocaleQuery, UserRole } from '@/types/graphql'
import moment from 'moment'
import { Component, Vue } from 'vue-property-decorator'
import { getModule } from 'vuex-module-decorators'
import UserModule from '../../store/modules/user'
import { faHome, faUser } from '@fortawesome/free-solid-svg-icons'
import DepartmentSubmenu from './Submenu/Department.vue'
import { ElMenu } from 'element-ui/types/menu'

@Component({
  name: 'NavBar',
  components: {
    DepartmentSubmenu
  }
})
export default class extends Vue {
  // ➊ Props

  // ➋ Data
  private UserRole = UserRole;

  private userModule = getModule(UserModule, this.$store);

  private homeIcon = faHome;

  private userIcon = faUser;

  // ➌ Computed

  private get now() {
    return moment(new Date()).format('DD.MM.YYYY')
  }

  private get name() {
    return this.userModule.name
  }

  // ➍ Watchers

  // ➎ Hooks

  // ➏ Methods

  private changeLocale(language: string) {
    let locale
    switch (language) {
      case 'fr':
        locale = Locale.Fr
        moment.locale('fr')
        break
      default:
        moment.locale('en')
        locale = Locale.En
        break
    }
    this.$apollo.mutate(
      {
        mutation: SetUserLocaleQuery,
        variables: {
          locale
        }
      }
    ).then(() => {
      this.$i18n.locale = language
      localStorage.language = language
    })
  }

  private signOut() {
    this.userModule.logOut()
    this.$router.push({ name: 'login' })
    window.location.reload()
  }

  private handleSubmenuSelect(menuIndex: string) {
    const isDepartmentMenuClick = menuIndex.startsWith('4')

    if (isDepartmentMenuClick) {
      this.$nextTick(() => {
        this.$refs.topNav && (this.$refs.topNav as ElMenu).open('4')
      })
    }
  }

  private handleMenuClose(menuIndex: string) {
    // If department context dropdown is closed
    if (menuIndex === '4') {
      this.$nextTick(() => {
        if (this.$refs.departmentSubmenu) {
          (this.$refs.departmentSubmenu as DepartmentSubmenu).changeDepartmentContext()
        }
      })
    }
  }
}
</script>

<style lang="scss">
.top-nav-box {
  border: none !important;
  background: none !important;
  height: 5vh !important;
  * {
    color: white !important;
    border: none !important;
  }
  .home-box {
    display: flex;
    justify-content: center;
    align-items: center;
    line-height: unset !important;
    height: 5vh !important;
    font-size: 24px;
  }
  .date-box {
    display: flex;
    justify-content: center;
    align-items: center;
    line-height: unset !important;
    height: 5vh !important;
    pointer-events: none;
  }
  .flag-box {
    display: flex;
    justify-content: center;
    align-items: center;
    float: right !important;
    height: 5vh !important;
    padding: 0 10px 0 10px;
    margin-left: 10px !important;
  }
  .el-menu-item span {
    padding-top: 3px;
  }
  .el-menu-item:hover {
    background: hsla(0, 0%, 100%, 0.4) !important;
  }
  .el-menu-item:focus {
    background: none !important;
  }
  .dropdown-menu-box {
    height: 5vh !important;
    float: right !important;
    padding-left: 10px ;
    padding-right: 10px ;
    div:hover {
      background: hsla(0, 0%, 100%, 0.4) !important;
    }
  }
  .el-menu--popup {
    margin-top: 1px !important;
    margin: 0 10px 0 10px !important;
    background-color: hsl(0, 0%, 100%) !important;
    box-shadow: 0 0 10px hsla(0, 0%, 0%, 0.2) !important;
    .dropdown-menu-item {
      color: hsl(0, 0%, 40%) !important;
      background: none !important;
      i {
        color: inherit !important;
      }
      &:hover {
        color: hsl(0, 0%, 0%) !important;
        background-color: hsl(0, 0%, 90%) !important;
        i {
          color: inherit !important;
        }
      }
    }
  }
  .el-submenu__title {
    display: flex;
    align-items: center;
    padding: 0 10px 0 10px;
    height: 5vh !important;
    line-height: unset !important;
    span {
      display: flex;
      gap: 5px;
      line-height: 0;
      // justify-content: flex-end;
      align-items: center;
      // background: hsla(270, 100%, 70%, 0.8) !important;
      svg {
        // justify-self: center;
        height: 16px;
        width: 16px;
        opacity: 0.9;
      }
    }
  }
}
</style>
